import { HStack, PageContainer, PageHeader, Spread, Text } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import usePermissions from '@/hooks/permissions/usePermissions'
import {
  exportProvinceOrderCutoffs,
  useProvinceOrderCutoffList,
  useUpdateProvinceOrderCutoffs,
} from '@/hooks/queries/credit/useProvinceOrderCutoffs'
import { UpdateProvinceOrderCutoffs } from '@repo/backend/credit'
import { PROVINCE_CODES, type ProvinceCode } from '@repo/constants'
import { hasPermissions } from '@repo/permissions'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import OrderCutoffDataTable, { OrderCutoffRow } from './-components/OrderCutoffDataTable'

export const Route = createFileRoute('/_auth/_modules/credit/operations/order-cutoffs/')({
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation('credit')
  const { permissions } = usePermissions()
  const canEdit = hasPermissions(permissions, 'credit_order_cutoff_edit')

  const [editedValues, setEditedValues] = useState<Map<string, string>>(new Map())

  const { data, isPending, error } = useProvinceOrderCutoffList()

  const { mutate: updateCutoffs, isPending: isSaving } = useUpdateProvinceOrderCutoffs({
    handleOnSuccess: () => setEditedValues(new Map()),
  })

  // Every province is listed whether or not it has a row yet, so an unconfigured one is visibly
  // blank rather than absent — it falls back to the default cutoff until someone sets it.
  const persistedByProvince = new Map((data?.results ?? []).map((row) => [row.provinceCode, row]))
  const results: OrderCutoffRow[] = PROVINCE_CODES.map((provinceCode) => ({
    provinceCode,
    cutoffTime: persistedByProvince.get(provinceCode)?.cutoffTime ?? null,
  }))

  const originalByProvince = new Map(results.map((row) => [row.provinceCode, row]))

  const handleValueChange = useCallback(
    (provinceCode: ProvinceCode, value: string) => {
      setEditedValues((previous) => {
        const next = new Map(previous)
        const original = originalByProvince.get(provinceCode)

        // Editing back to the stored value drops the pending change rather than saving a no-op.
        if (original && value === (original.cutoffTime ?? '')) {
          next.delete(provinceCode)
          return next
        }

        next.set(provinceCode, value)
        return next
      })
    },
    [originalByProvince],
  )

  const getDisplayValue = useCallback(
    (row: OrderCutoffRow): string => editedValues.get(row.provinceCode) ?? row.cutoffTime ?? '',
    [editedValues],
  )

  const handleSaveChanges = () => {
    if (editedValues.size === 0) return

    const updates: UpdateProvinceOrderCutoffs['updates'] = []
    editedValues.forEach((cutoffTime, provinceCode) => {
      updates.push({ provinceCode, cutoffTime })
    })

    updateCutoffs({ updates })
  }

  const areChangesPending = editedValues.size > 0

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={areChangesPending} />

      <Spread className="items-center">
        <PageHeader>{t('provinceOrderCutoffs.pageHeader')}</PageHeader>
        <HStack gap="sm">
          <ExportCsvButton
            label={t('provinceOrderCutoffs.export')}
            getCsvExport={() => exportProvinceOrderCutoffs()}
            rowCount={results.length}
          />
        </HStack>
      </Spread>

      <Text size="md" tone="muted" className="text-pretty">
        {t('provinceOrderCutoffs.description')}
      </Text>

      <OrderCutoffDataTable
        data={results}
        isPending={isPending}
        error={error}
        editedValues={editedValues}
        canEdit={canEdit}
        getDisplayValue={getDisplayValue}
        onValueChange={handleValueChange}
      />

      <PermissionsGuard required={['credit_order_cutoff_edit']}>
        <PendingChanges
          areChangesPending={areChangesPending}
          isSavePending={isSaving}
          onSaveChanges={handleSaveChanges}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
